/**
 * Security tab.
 *
 * Parity target: Angular `securityTemplate` (config.component.html
 * lines 1539–1666). Covers Google Captcha toggle, session timeout,
 * OTP SMS template + backup, and in-app notification banner.
 */

import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import {
  buildSecurityPayload,
  saveConfig,
} from '../../services/configService'
import type { OnOff, SecurityFormValues } from '../../types/config'
import {
  FieldLegend,
  integerTextInputAttrs,
  LabeledInput,
  LabeledTextArea,
  SegmentedButtonGroup,
  } from './formPrimitives'
import { SectionCard } from '@/components/ui/SectionCard'

import type { SecurityTabProps } from '../../types'

const EMPTY: SecurityFormValues = {
  GOOGLE_CAPTCHA: '',
  SESSION_TIMEOUT: '',
  OTP_SMS_CONTENT: '',
  OTP_BACKUP: '',
  NOTIFICATION_SHOW: '',
  NOTIFICATION_CONTENT: '',
  NOTIFICATION_TIME: '',
}

export function SecurityTab({ initialValues, isLoading, onSaved }: SecurityTabProps) {
  const [submitLoading, setSubmitLoading] = useState(false)
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty, isValid },
  } = useForm<SecurityFormValues>({ defaultValues: EMPTY, mode: 'onChange' })

  useEffect(() => {
    if (initialValues) reset(initialValues)
  }, [initialValues, reset])

  const onSubmit = async (values: SecurityFormValues) => {
    try {
      setSubmitLoading(true)
      await saveConfig(buildSecurityPayload(values))
      showSuccess('Config Change Successfully!')
      reset(values)
      onSaved()
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitLoading(false)
    }
  }

  if (isLoading) {
    return <ModuleScreenLoading minHeight="300px" />
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <SectionCard number={1} title="Security">
        <div className="flex flex-wrap gap-8">
          <FieldLegend label="Google Captcha">
            <SegmentedButtonGroup
              ariaLabel="Google Captcha"
              value={watch('GOOGLE_CAPTCHA') ?? ''}
              onChange={(v) =>
                setValue('GOOGLE_CAPTCHA', v as OnOff, { shouldDirty: true, shouldValidate: true })
              }
              options={[
                { value: 'ON', label: 'ON' },
                { value: 'OFF', label: 'OFF' },
              ]}
            />
          </FieldLegend>
          <div className="max-w-xs">
            <LabeledInput
              label="Session Timeout (Minutes)"
              {...integerTextInputAttrs}
              {...register('SESSION_TIMEOUT')}
            />
          </div>
        </div>
        <div className="mt-4 max-w-2xl">
          <LabeledTextArea
            label="OTP SMS Content"
            hint="Available variables: $otp_number, $username"
            rows={5}
            {...register('OTP_SMS_CONTENT')}
          />
        </div>
        <div className="mt-3 max-w-sm">
          <LabeledInput label="OTP Backup" {...register('OTP_BACKUP')} />
        </div>
      </SectionCard>

      <SectionCard number={2} title="Notification">
        <FieldLegend label="Show Notification">
          <SegmentedButtonGroup
            ariaLabel="Show notification banner"
            value={watch('NOTIFICATION_SHOW') ?? ''}
            onChange={(v) =>
              setValue('NOTIFICATION_SHOW', v as OnOff, { shouldDirty: true, shouldValidate: true })
            }
            options={[
              { value: 'ON', label: 'ON' },
              { value: 'OFF', label: 'OFF' },
            ]}
          />
        </FieldLegend>
        <div className="mt-4 max-w-2xl">
          <LabeledTextArea
            label="Notification Content"
            rows={5}
            {...register('NOTIFICATION_CONTENT')}
          />
        </div>
        <div className="mt-3 max-w-xs">
          <LabeledInput
            label="Notification Time (Seconds)"
            {...integerTextInputAttrs}
            {...register('NOTIFICATION_TIME')}
          />
        </div>
      </SectionCard>

      <div>
        <button
          type="submit"
          disabled={!isDirty || submitLoading || !isValid}
          className={primaryEntityActionButtonClass}
        >
          {submitLoading ? 'Saving…' : 'Submit'}
        </button>
      </div>
    </form>
  )
}
